import { DateTime } from 'luxon';
import { action, IObservableArray, observable, makeObservable } from 'mobx';
import { v4 as uuid } from 'uuid';
import IDeletionStack from '../classes/IDeletionStack';
import IFormWorkflow from '../classes/IFormWorkflow';
import { AppService } from './AppService';
import { EFetchState, NotificationService } from './NotificationService';
import { OrderDetails } from './OrderDetails';
import { RequirementsExceptionsData } from './RequirementsExceptionsData';
import {
  deleteCommitmentAsync,
  getCommitmentExceptionsAndInitializeAsync,
  getExceptionsTemplatesAsync,
  getOrderAsync,
  saveRequirementsExceptionsAsync,
} from './RestCommitmentsExceptionsService';
import { SpecificReqExcData } from './SpecificReqExcData';
import { finalizeAsync } from 'API/order/TitleReportsAPI';
import removeLineBreaks from 'policyUI/utils/removeLineBreaks';
import { UUID } from '../../types/shared/UUID';
import { Logger } from '../../ui-toolkit/logging/LoggerService';

export interface ICommitmentException {
  body: string;
  note?: string;
  created_at: string;
  filled_out: boolean;
  id: string;
  code?: string;
  order_id: string;
  position: number;
  schedule: ReqExcSections;
  subject?: string;
  template_id: string;
  template_version: number;
}

export interface ISpecificException {
  template: string;
  note?: string;
  id: string;
  schedule: ReqExcSections;
  subject?: string;
  code?: string;
  appliesOnlyInCounties: string[];
  appliesOnlyInStates: string[];
  appliesTo: string[];
}

export enum ReqExcSections {
  B1 = 'B1',
  B2 = 'B2',
}

interface IDeleteItem {
  item: ICommitmentException;
  sectionName: string;
}

export class RequirementsExceptionsService implements IDeletionStack, IFormWorkflow {
  public savedIds: Set<string> = new Set('');
  public deletionStack: IDeleteItem[] = [];

  public formData = new RequirementsExceptionsData();
  public formDataChanged: boolean = false;
  public allSpecificExceptions: ISpecificException[] = [];
  public specificExceptions: SpecificReqExcData = new SpecificReqExcData();

  private readonly appData: AppService;
  private notifications: NotificationService;

  constructor(appData: AppService, notifications: NotificationService) {
    makeObservable(this, {
      formData: observable,
      formDataChanged: observable,
      allSpecificExceptions: observable,
      specificExceptions: observable,
      setAllSpecificExceptions: action,
      setFormDataChanged: action,
      setSpecificExceptions: action,
      setSectionItems: action,
      setChangedText: action,
      removeItemFromData: action,
      addItem: action,
      addCustomItem: action,
      onOrderChanged: action,
      addToDeletionStack: action,
      removeFromDeletionStack: action,
      resetDeletionStack: action,
      init: action,
      save: action,
      finalize: action,
    });

    this.appData = appData;
    this.notifications = notifications;
  }

  /**
   * Setters
   */
  public setAllSpecificExceptions(specificExc: ISpecificException[]) {
    this.allSpecificExceptions = specificExc;
  }

  public setFormDataChanged(value: boolean) {
    this.formDataChanged = value;
  }

  public setSpecificExceptions(specificExcFilter: ISpecificException[]) {
    const specificReqExs: SpecificReqExcData = new SpecificReqExcData();

    specificExcFilter.forEach((item: ISpecificException) => {
      specificReqExs[item.schedule].push(item);
    });

    this.specificExceptions = specificReqExs;
  }

  public setSectionItems(sectionName: ReqExcSections, items: ICommitmentException[]) {
    this.appData.setErrorMessage('');
    this.formData = {
      ...this.formData,
      [sectionName]: items,
    };
    this.setFormDataChanged(true);
  }

  public setChangedText(sectionName: ReqExcSections, newText: string, item: ICommitmentException) {
    const newList = this.formData[sectionName].slice();
    const index = newList.indexOf(item);
    newList[index] = {
      ...newList[index],
      body: removeLineBreaks(newText),
    };
    this.setSectionItems(sectionName, newList);
  }

  public removeItemFromData(sectionName: ReqExcSections, targetItem: ICommitmentException) {
    this.appData.setErrorMessage('');
    (this.formData[sectionName] as IObservableArray).remove(targetItem);
    this.setFormDataChanged(true);
  }

  public addItem(sectionName: ReqExcSections, item: ISpecificException) {
    this.appData.setErrorMessage('');
    const body = removeLineBreaks(item.template);

    const newItem: ICommitmentException = {
      body,
      created_at: DateTime.local().toUTC().toISO({ includeOffset: false }),
      filled_out: true,
      id: uuid(),
      note: item.note,
      code: item.code,
      order_id: this.appData.order.order_id,
      position: this.formData[sectionName].length,
      schedule: item.schedule,
      subject: item.subject,
      template_id: item.id,
      template_version: 1,
    };

    if (this.formData[sectionName].map((formDataItem) => formDataItem.body).indexOf(newItem.body) === -1) {
      const items = [...this.formData[sectionName], newItem];
      this.setSectionItems(sectionName, items);
      this.setFormDataChanged(true);
    } else {
      this.appData.setErrorMessage(
        `This ${sectionName === ReqExcSections.B1 ? 'requirement' : 'exception'} already exists`
      );
    }
  }

  public addCustomItem(sectionName: ReqExcSections, customText: string) {
    this.appData.setErrorMessage('');
    const newItem = {
      body: customText,
      created_at: DateTime.local().toUTC().toISO({ includeOffset: false }),
      filled_out: true,
      id: uuid(),
      code: '',
      order_id: this.appData.order.order_id,
      position: this.formData[sectionName].length,
      schedule: sectionName,
      subject: '',
      template_id: uuid(),
      template_version: 1,
    };

    const items = [...this.formData[sectionName], newItem];

    this.setSectionItems(sectionName, items);
    this.setFormDataChanged(true);
  }

  public onOrderChanged(sectionName: ReqExcSections, newArray: ICommitmentException[]) {
    this.appData.setErrorMessage('');
    this.setSectionItems(sectionName, this.changePositionNumber(newArray));
    this.setFormDataChanged(true);
  }

  public addToDeletionStack(sectionName: ReqExcSections, newItem: ICommitmentException) {
    this.appData.setErrorMessage('');
    const isOnStack = this.deletionStack.map(({ item }) => item.id).indexOf(newItem.id) !== -1;
    const isInSavedData = this.savedIds.has(newItem.id);

    this.removeItemFromData(sectionName, newItem);

    if (!isOnStack && isInSavedData) {
      this.deletionStack = [...this.deletionStack, { sectionName, item: newItem }];
      this.setFormDataChanged(true);
    }
  }

  public removeFromDeletionStack = (targetItem: ICommitmentException) => {
    this.appData.setErrorMessage('');
    this.deletionStack = [...this.deletionStack.filter(({ item }) => item.id !== targetItem.id)];
    this.setFormDataChanged(true);
  };

  public resetDeletionStack = () => {
    this.deletionStack = [];
  };

  public init = async (orderId: UUID) => {
    this.notifications.setSendState(EFetchState.START);
    await this.executeFetchOrderWithDetails(orderId);
    await this.executeFetchSpecificRequirementsExceptions(orderId);
    this.notifications.setSendState(EFetchState.SUCCESS);
    this.setFormDataChanged(false);
  };

  public save = async (orderId: UUID) => {
    this.notifications.resetAllButAuthState();
    this.notifications.setSaveState(EFetchState.START);
    await this.executeDelete(orderId);
    await this.executeSave(orderId);
    await this.executeFetchOrderWithDetails(orderId);
    this.notifications.setSaveState(EFetchState.SUCCESS);
  };

  public finalize = async (orderId: UUID) => {
    this.notifications.resetAllButAuthState();
    this.notifications.setFinalizeState(EFetchState.START);
    await this.executeDelete(orderId);
    await this.executeSave(orderId);
    await this.executeFetchOrderWithDetails(orderId);
    await this.executeFinalize(orderId);
    this.notifications.setFinalizeState(EFetchState.SUCCESS);
  };

  private changePositionNumber(array: ICommitmentException[]): ICommitmentException[] {
    for (let i = 0; i < array.length; i += 1) {
      array[i].position = i;
    }
    return array;
  }

  private getInitReqExs(items: ICommitmentException[]): ICommitmentException[] {
    const newItems: ICommitmentException[] = [];
    const specExc: ISpecificException[] = this.allSpecificExceptions;
    items.forEach((item: ICommitmentException) => {
      const newItem = specExc.find((s: ISpecificException) => {
        return s.id === item.template_id;
      });
      if (newItem) {
        newItems.push({
          ...item,
          body: removeLineBreaks(item.body),
          note: newItem.note,
        });
      } else {
        newItems.push(item);
      }
    });
    return newItems
      .sort((a: ICommitmentException, b: ICommitmentException) =>
        a.position > b.position ? 1 : b.position > a.position ? -1 : 0
      )
      .map((item, i) => ({ ...item, position: i }));
  }

  private filterSpecificExceptions() {
    const { transaction_type, state, county_fips } = this.appData.order;

    const exceptions = [...this.allSpecificExceptions];

    // applies to transaction type
    let filtered = exceptions.filter((item) => {
      return item.appliesTo.includes(transaction_type);
    });

    // applies to state
    filtered = filtered.filter((item) => {
      return item.appliesOnlyInStates.includes(state) || item.appliesOnlyInStates.length === 0;
    });

    // applies to county
    filtered = filtered.filter((item) => {
      return item.appliesOnlyInCounties.includes(county_fips) || item.appliesOnlyInCounties.length === 0;
    });

    this.setSpecificExceptions(filtered);
  }

  /**
   * HTTP Calls executions
   */

  private executeFetchOrderWithDetails = async (orderId: UUID) => {
    this.appData.setErrorMessage('');

    getOrderAsync(orderId)
      .then((response: any) => {
        const data = response.data;
        this.appData.setFinalizable(data.finalizable);
        this.appData.setOrderData(
          new OrderDetails({
            order_id: data.id,
            county_fips: data.fips,
            state: data.propertyAddress.stateAbbreviation,
            transaction_type: data.transactionCategory,
          })
        );
      })
      .catch((error) => this.handleError(orderId, error));

    try {
      const response_1 = (await getCommitmentExceptionsAndInitializeAsync(orderId)) as any;
      const data_1 = response_1.data;

      const requirements = this.getInitReqExs(data_1.filter((row: any) => row.schedule === ReqExcSections.B1));
      const exceptions = this.getInitReqExs(data_1.filter((row_1: any) => row_1.schedule === ReqExcSections.B2));

      const savedIds = [...requirements, ...exceptions].map((e) => e.id);

      this.savedIds = new Set(savedIds);

      this.setSectionItems(ReqExcSections.B1, requirements);
      this.setSectionItems(ReqExcSections.B2, exceptions);

      this.setFormDataChanged(false);
    } catch (error) {
      return this.handleError(orderId, error);
    }
  };

  private handleError(orderId: UUID, error: any) {
    this.notifications.setSendState(EFetchState.FAILURE);
    this.appData.setErrorMessage(`${error.response.status} ${error.response.statusText}`);
    const { token } = this.appData;
    Logger.error('Error getting initial requirements and exceptions', { orderId, token });
    throw new Error(error);
  }

  private executeFetchSpecificRequirementsExceptions = async (orderId: UUID) => {
    this.appData.setErrorMessage('');
    try {
      const response = await getExceptionsTemplatesAsync();
      const exceptions = response.data;
      this.setAllSpecificExceptions(exceptions);
      this.setSpecificExceptions(exceptions);

      this.setSectionItems(ReqExcSections.B1, this.getInitReqExs(this.formData[ReqExcSections.B1]));

      this.setSectionItems(ReqExcSections.B2, this.getInitReqExs(this.formData[ReqExcSections.B2]));

      this.filterSpecificExceptions();
      this.setFormDataChanged(false);
    } catch (error: any) {
      this.notifications.setSendState(EFetchState.FAILURE);
      this.appData.setErrorMessage(`${error.response.status} ${error.response.statusText}`);
      const { token } = this.appData;
      Logger.error('Error getting initial specific requirements and exceptions', { orderId, token });
      throw new Error(error);
    }
  };

  private executeDelete = async (orderId: UUID) => {
    const itemIds = this.deletionStack.map(({ item }) => item.id);
    return deleteCommitmentAsync(orderId, itemIds)
      .then(() => {
        this.resetDeletionStack();
        this.setFormDataChanged(true);
      })
      .catch((error) => {
        this.notifications.setSendState(EFetchState.FAILURE);
        this.appData.setErrorMessage('There was an error deleting items through Commitments Exceptions Service');
        const { token } = this.appData;
        Logger.error('Error saving requirements and exceptions', { orderId, token, item: itemIds });
        throw new Error(error);
      });
  };

  private executeSave = async (orderId: UUID) => {
    this.appData.setErrorMessage('');
    try {
      await saveRequirementsExceptionsAsync(orderId, [...this.formData.B1, ...this.formData.B2]);
      this.setFormDataChanged(false);
    } catch (error: any) {
      this.notifications.setSaveState(EFetchState.FAILURE);
      this.appData.setErrorMessage(`${error.response.status} ${error.response.statusText}`);
      const { token } = this.appData;
      Logger.error('Error saving requirements and exceptions', { orderId, token, formData: this.formData });
      throw new Error(error);
    }
  };

  private executeFinalize = (orderId: UUID) => {
    if (!this.appData.finalizable) return this.appData.setErrorMessage("Can't finalize. Please check entered data.");
    this.appData.setErrorMessage('');
    return finalizeAsync(orderId).catch((error) => {
      this.notifications.setFinalizeState(EFetchState.FAILURE);
      this.appData.setErrorMessage(`${error.response.status} ${error.response.statusText}`);
      const { token } = this.appData;
      Logger.error('Error finalizing requirements and exceptions', { orderId, token });
      throw new Error(error);
    });
  };
}
